import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

import { showUnauthorizedToast } from 'src/utils/message';
import { DocumentDownloadIdKey, DocumentDownloadRequestParams } from '../../types/document';
import { headers, timeOut } from '../../utils/constants';

import { ApiResponseResult } from '../../types/shared';
import { constants } from '../../utils/settings';
import { downloadMessages } from '../../shared/constants';
import { track } from '../../utils/telemetry';
import { tryCatchLog } from 'src/utils/errors';

// const getToken = () => getCookie(tokenCookieName);

const axiosCreate = { baseURL: constants.api.url, timeout: timeOut, headers};
const axiosCreateWithAuthentication = { baseURL: constants.api.url, timeout: timeOut, headers, withCredentials: true};
export const axiosClient = axios.create(axiosCreate);

export const axiosClientWithAuthentication = axios.create(axiosCreateWithAuthentication);

// TOREFACTOR - move auth credentials out of code
axiosClientWithAuthentication.interceptors.request.use(
  config => {
    // if (config.baseURL && config.headers && !config.headers.Authorization) {
    //   const token = getCookie(tokenCookieName);
    //   if (token) {
    //     config.headers.Authorization = `Bearer ${token}`;
    //   }
    // }

    // return config

    if (config.timeout === undefined || config.timeout === 0) {
      return config;
    }

    const source = axios.CancelToken.source();

    setTimeout(() => {
      source.cancel(
        `Cancelled request. Took longer than ${config.timeout}ms to get complete response.`,
      );
    }, config.timeout);

    // If caller configures cancelToken, preserve cancelToken behavior.
    if (config.cancelToken) {
      config.cancelToken.promise.then(cancel => {
        source.cancel(cancel.message);
      });
    }
    return { ...config, cancelToken: source.token };
  },
  error => Promise.reject(error),
);

const handleResponseError = (error: unknown) => {
  if (!axios.isAxiosError(error)) {
    return Promise.reject(error);
  }
  const { response, message = '' } = error;
  if (message === `Cancelled request. Took longer than ${timeOut}ms to get complete response.`) {
    const abortController = new AbortController();
    abortController.abort();
  }
  if (response && response.status === 401) {
    console.error('api - 401', error);
    showUnauthorizedToast(error.config?.url);
  }
  return Promise.reject(error);
};
axiosClient.interceptors.response.use(undefined, handleResponseError);
axiosClientWithAuthentication.interceptors.response.use(undefined, handleResponseError);

const getRequest = (requestUrl = '', userOptions = {}) => {
  try {
    return axiosClient({ method: 'GET', url: requestUrl, ...userOptions });
  } catch (error) {
    track('API_ERROR', { error, method: 'GET', requestUrl, userOptions });
    return null;
  }
};

// const getRequestWithCredentials = (requestUrl = '', userOptions = {}) => {
//   try {
//     return axiosClient({ method: 'GET', url: requestUrl, withCredentials: true, ...userOptions });
//   } catch (error) {
//     track('API_ERROR', { error, method: 'GET', requestUrl, userOptions });
//     return null;
//   }
// };

/**
 * Direct Axios request with typing enforced.
 * TODO: response should be raw. create a base request,
 *  or update the hard-coded expectation of an `ApiResponseResult` typed result (`data.result`)?
 * @param {AxiosRequestConfig} config: request param object
 * @param {boolean} useAuthentication: header, defaults to true
 * @param {string} resultKey: optional key for result object to accommodate legacy service response shape
 * @returns {ApiResponseResult<T>} generically typed Promise
 */
export async function axiosRequest<T>(config: AxiosRequestConfig, useAuthentication = false) {
  const axiosInstance: AxiosInstance =
    useAuthentication === true ? axiosClientWithAuthentication : axiosClient;
  return tryCatchLog(async () => {
    const response = await axiosInstance.request<ApiResponseResult<T>>(config);
    return response?.data?.result;
  }, config);
}

export async function axiosBareRequest<T>(config: AxiosRequestConfig, useAuthentication = false) {
  const axiosInstance: AxiosInstance =
    useAuthentication === true ? axiosClientWithAuthentication : axiosClient;

  try {
    const response = await axiosInstance.request<T>(config);
    return response?.data;
  } catch (error: any) {
    track('API_ERROR', {
      error,
      method: config?.method || 'GET',
      axiosRequestConfig: config?.url,
      config,
    });
    // TOREFACTOR - standardize error handling and reporting, failing silently, etc.
    console.error(`axiosRequest ERROR: \n${error}`);
    console.error(error);
    return {} as T;
  }
}

/**
 * Helper fn for authenticated requests.
 * @param config
 * @returns a typed Promise
 */
export async function axiosRequestAuthenticated<T>(config: AxiosRequestConfig) {
  return axiosRequest<T>(config, true);
}

/**
 * Helper fn for authenticated requests.
 * @param config
 * @returns a typed Promise
 */
export async function axiosBareRequestAuthenticated<T>(config: AxiosRequestConfig) {
  return axiosBareRequest<T>(config, true);
}

/**
 * Helper fn for authenticated POST requests.
 * @param config: AxiosRequestConfig
 * @returns a typed Promise
 */
export async function axiosPostAuthenticated<T>(config: AxiosRequestConfig) {
  return axiosRequestAuthenticated<T>({ ...config, method: 'POST' });
}

export async function axiosFormDataPostAuthenticated<T>(config: AxiosRequestConfig) {

  const headers = {
    ...(config.headers || {}),
    'Content-Type': 'multipart/form-data',
  };

  const updatedConfig: AxiosRequestConfig = {
    ...config,
    headers,
    method: 'POST',
  };
  return axiosRequestAuthenticated<T>(updatedConfig);
}

/**
 * Helper fn for authenticated POST requests.
 * @param config: AxiosRequestConfig
 * @returns a typed Promise
 */
export async function axiosBarePostAuthenticated<T>(config: AxiosRequestConfig) {
  return axiosBareRequestAuthenticated<T>({ ...config, method: 'POST' });
}
export async function axiosFormDataBarePostAuthenticated<T>(config: AxiosRequestConfig) {

  const headers = {
    ...(config.headers || {}),
    'Content-Type': 'multipart/form-data',
  };

  const updatedConfig: AxiosRequestConfig = {
    ...config,
    headers,
    method: 'POST',
  };
  return axiosBareRequestAuthenticated<T>(updatedConfig);
}

/**
 * Helper fn for authenticated download requests.
 * @param config
 */
export async function axiosRequestDownloadAuthenticated<T>(config: AxiosRequestConfig) {
  return axiosRequest<T>(config, true);
}

const postRequest = (requestUrl = '', data = {}, userOptions = {}) => {
  try {
    return axiosClient({
      method: 'POST',
      url: requestUrl,
      ...userOptions,
      data,
    });
  } catch (error) {
    track('API_ERROR', {
      error,
      method: 'POST',
      requestUrl,
      data,
      userOptions,
    });
    return undefined;
  }
};


const getRequestWithAuthentication = (requestUrl = '', userOptions = {}) => {
  try {
    return axiosClientWithAuthentication({
      method: 'GET',
      url: requestUrl,
      ...userOptions,
    });
  } catch (error) {
    track('API_ERROR', { error, method: 'GET', requestUrl, userOptions });
    return undefined;
  }
};

const postRequestWithAuthentication = (requestUrl = '', data = {}, userOptions = {}) => {
  try {
    return axiosClientWithAuthentication({
      method: 'POST',
      url: requestUrl,
      ...userOptions,
      data,
      withCredentials:true,
    });
  } catch (error) {
    track('API_ERROR', {
      error,
      method: 'POST',
      requestUrl,
      data,
      userOptions,
    });
    return undefined;
  }
};

const postFormDataRequestWithAuthentication = (requestUrl = '', data = {}, userOptions: AxiosRequestConfig = {}) => {
  try {
    const headers = { ...userOptions.headers, 'Content-Type': 'multipart/form-data' };
    const axiosConfig: AxiosRequestConfig = {
      method: 'POST',
      url: requestUrl,
      ...userOptions,
      data,
      headers,
    };
    return axiosClientWithAuthentication(axiosConfig);
  } catch (error) {
    track('API_ERROR', {
      error,
      method: 'POST',
      requestUrl,
      data,
      userOptions,
    });
    return undefined;
  }
};

/**
 * Typed, authenticated Axios download GET request.
 * This also adds, programmatically clicks, then removes an `<a href.../>` anchor.
 * Auth token is sent via the header rather than appended to the URL.
 * @param requestParams: DocumentDownloadRequestParams
 */
export async function downloadRequestAuthenticated(requestParams: DocumentDownloadRequestParams) {
  const { baseUrl, docIdParams: data, requestUrl } = requestParams;
  const docIdKeys = Object.keys(data);
  const queryString = docIdKeys
    .map(key => {
      const value = data[key as DocumentDownloadIdKey] || '';
      return encodeURIComponent(key) + '=' + encodeURIComponent(value);
    })
    .join('&');

  const downloadUrl = `${baseUrl}${requestUrl}?${queryString}`;

  // Request the document.
  try {
    // const response = await axios.request({
    //   url: downloadUrl,
    //   method: 'GET',
    //   // headers: {
    //   //   Authorization: `Bearer ${getToken()}`,
    //   // },
    //   withCredentials:true,
    //   responseType: 'blob',
    // });
    const response = await axiosClientWithAuthentication.get(downloadUrl, {
      responseType: 'blob',
    });
    const responseData = response.data;

    let fileName;

    // Parse the response header content-disposition for the actual file name.
    const contentDisposition = response?.headers['content-disposition'] || '';
    if (contentDisposition !== '') {
      const contentDispositionAttributes = contentDisposition.split(';');
      fileName = contentDispositionAttributes.find(
        (cont: string) => cont.trim().indexOf('filename=') >= 0,
      );
      fileName = fileName?.replace('filename=', '').replace(/"/g, '').trim();
    }

    // Throw an error if the fileName can't be determined.
    if (!fileName) {
      throw new Error(downloadMessages.fileNameNotFound);
    }

    // Create a blob URL representing the data.
    const blob = new Blob([responseData], { type: response?.data?.type || '' });
    const windowUrl = window.URL.createObjectURL(blob);

    // Attach the blob URL to anchor element with download attribute
    const anchor = document.createElement('a');
    anchor.setAttribute('href', windowUrl);
    anchor.setAttribute('target', '_blank');
    anchor.setAttribute('download', fileName);
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(windowUrl);
  } catch (error) {
    console.error(error);
  }
}

const downloadFileWithAuthentication = (requestUrl = '', data = {} as any, baseUrl = '') => {
  try {
    const docIdKeys = Object.keys(data);
    const queryString = docIdKeys
      .map((key: string) => {
        return encodeURIComponent(key) + '=' + encodeURIComponent(data[key]);
      })
      .join('&');

    const link = document.createElement('a');
    // link.href = `${baseUrl}${requestUrl}?${queryString}${queryString && '&'}tk=${getToken()}`;
    link.href = `${baseUrl}${requestUrl}?${queryString}`;

    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    track('API_ERROR', { error, requestUrl, data, baseUrl });
    return undefined;
  }
};

const api = {
  downloadFileWithAuthentication,
  getRequest,
  getRequestWithAuthentication,
  postRequest,
  postRequestWithAuthentication,
  postFormDataRequestWithAuthentication,
};

export default api;
